// ── PropertyAIChat UI strings (English / Spanish) ─────────────────────────────
// The AI model already answers in the user's language, but confirmation messages
// and action-card copy are rendered by the frontend. These are hardcoded per
// language (EN + ES only, per PROPS-3320) and picked by detectLang() below.

export const DEFAULT_LANG = 'en'

// Error card shown when a reply fails (ATAI-556). English only, the same as
// the AI Search card it reuses: the ticket and PRD give English copy only.
export const ERROR_CARD = {
  title: "We couldn't complete your request",
  body: 'Something went wrong while AI Intelligence was processing your request. Please try again or rephrase your search.',
}

export const CHAT_STRINGS = {
  en: {
    calcLabels: {
      update_rental_roi: 'Rental ROI Calculator',
      update_fix_flip: 'Fix & Flip Analyzer',
    },

    // Confirmation messages inserted after a tool action
    calcUpdated: (calcLabel) => `Done — I've updated the ${calcLabel}. The results panel now reflects the new values.`,
    openingCalculators: 'Opening the calculators and applying the changes…',
    alreadySaved: 'This property is already saved.',
    propertySaved: 'Done — property saved to your list.',
    skipTraceInitiated: 'Skip trace initiated. The owner contact info will appear once available.',
    skipTraceNoResults: 'Skip trace complete — no phone numbers or emails were found for this property\'s owner.',
    skipTraceAlreadyDone: 'This owner was already skip traced in the last 6 months, so no new search was run. Here\'s what we have:',
    skipTraceFailed: 'Sorry, the skip trace couldn\'t be completed. Please try again.',
    skipTraceResultsNotLoaded: 'The skip trace finished, but I couldn\'t load the results. Reopen the property to see them.',
    loadingComps: 'Loading comparable sales data for this property… I\'ll answer your question once it\'s ready.',
    compsLoadFailed: 'I couldn\'t load comparable sales data for this property right now.',
    creatingSummary: 'Creating a summary of this property…',
    error: (message) => `Sorry, I encountered an error: ${message}`,

    // Tool action card descriptions
    toolDescriptions: {
      save_property: 'Save this property to your list.',
      skip_trace_property: 'Run skip trace to find the owner’s phone and email.',
      open_rental_roi_calculator: 'Open the Rental ROI Calculator for this property.',
      open_fix_flip_calculator: 'Open the Fix & Flip Analyzer for this property.',
    },
    updateFallback: 'Update calculator inputs',
    fieldLabels: {
      listingPrice: 'Purchase price',
      downPaymentPct: 'Down payment',
      interestRate: 'Interest rate',
      mortgageYears: 'Mortgage term',
      rentMonthly: 'Monthly rent',
      otherRevMonthly: 'Other revenue',
      vacancyRate: 'Vacancy',
      propertyTaxPct: 'Property tax',
      managementFeePct: 'Management fee',
      maintenanceResPct: 'Maintenance',
      insuranceYearly: 'Insurance',
      utilitiesMonthly: 'Utilities',
      hoaYearly: 'HOA',
      appreciationPct: 'Appreciation',
      purchasePrice: 'Purchase price',
      holdingMonths: 'Holding period',
      salePrice: 'Sale price',
    },
    units: { perYear: '/yr', perMonth: '/mo', years: 'years', months: 'months' },

    // ToolActionCard
    toolCards: {
      update_rental_roi: { title: 'Rental ROI Calculator', applyText: 'Apply to Calculator', appliedText: 'Applied to calculator' },
      update_fix_flip: { title: 'Fix & Flip Analyzer', applyText: 'Apply to Calculator', appliedText: 'Applied to calculator' },
      save_property: { title: 'Save Property', applyText: 'Save Property', appliedText: 'Property saved' },
      skip_trace_property: { title: 'Skip Trace', applyText: 'Skip Trace', appliedText: 'Skip trace initiated' },
    },
    toolCardFallback: { title: 'Action', applyText: 'Apply', appliedText: 'Applied' },

    // OpenPromptCard
    openPrompt: {
      title: 'Open Calculator?',
      before: 'The ',
      after: ' isn\'t open yet. Open it to apply these changes?',
      button: 'Open & Apply',
    },

    // ViewCompsCard
    viewComps: {
      title: 'Comparables & Nearby Listings',
      prompt: 'View the full comparable sales data in the property details.',
      openedPrompt: 'The Comparables tab is now open.',
      button: 'Open Comparables Tab',
      done: 'Comparables tab opened',
    },

    // ActionCard (open calculators)
    actionCards: {
      open_rental_roi_calculator: {
        label: 'Rental ROI Calculator',
        prompt: 'Open the Rental ROI Calculator to run rental return scenarios.',
        button: 'Open Rental ROI Calculator',
        done: 'Rental ROI Calculator opened',
      },
      open_fix_flip_calculator: {
        label: 'Fix & Flip Analyzer',
        prompt: 'Open the Fix & Flip Analyzer to model a flip.',
        button: 'Open Fix & Flip Analyzer',
        done: 'Fix & Flip Analyzer opened',
      },
    },
    actionCardFallback: { label: 'Action', prompt: '', button: 'Open', done: 'Opened' },
    actionCardOpened: (label) => `The ${label} is now open.`,
  },

  es: {
    calcLabels: {
      update_rental_roi: 'Calculadora de ROI de Alquiler',
      update_fix_flip: 'Analizador de Fix & Flip',
    },

    // Calculator names differ in gender (la Calculadora / el Analizador), so
    // interpolated labels are introduced with the neutral "la herramienta".
    calcUpdated: (calcLabel) => `Listo — actualicé los datos en la herramienta ${calcLabel}. El panel de resultados ya refleja los nuevos valores.`,
    openingCalculators: 'Abriendo las calculadoras y aplicando los cambios…',
    alreadySaved: 'Esta propiedad ya está guardada.',
    propertySaved: 'Listo — la propiedad se guardó en tu lista.',
    skipTraceInitiated: 'Skip trace iniciado. La información de contacto del propietario aparecerá cuando esté disponible.',
    skipTraceNoResults: 'Skip trace completado — no se encontraron teléfonos ni correos del propietario de esta propiedad.',
    skipTraceAlreadyDone: 'Ya se hizo un skip trace de este propietario en los últimos 6 meses, así que no se hizo una nueva búsqueda. Esto es lo que tenemos:',
    skipTraceFailed: 'Lo siento, no se pudo completar el skip trace. Inténtalo de nuevo.',
    skipTraceResultsNotLoaded: 'El skip trace terminó, pero no pude cargar los resultados. Vuelve a abrir la propiedad para verlos.',
    loadingComps: 'Cargando los datos de ventas comparables de esta propiedad… Responderé tu pregunta en cuanto estén listos.',
    compsLoadFailed: 'No pude cargar los datos de ventas comparables de esta propiedad en este momento.',
    creatingSummary: 'Creando un resumen de esta propiedad…',
    error: (message) => `Lo siento, ocurrió un error: ${message}`,

    toolDescriptions: {
      save_property: 'Guardar esta propiedad en tu lista.',
      skip_trace_property: 'Ejecutar skip trace para encontrar el teléfono y el correo del propietario.',
      open_rental_roi_calculator: 'Abrir la Calculadora de ROI de Alquiler para esta propiedad.',
      open_fix_flip_calculator: 'Abrir el Analizador de Fix & Flip para esta propiedad.',
    },
    updateFallback: 'Actualizar los valores de la calculadora',
    fieldLabels: {
      listingPrice: 'Precio de compra',
      downPaymentPct: 'Pago inicial',
      interestRate: 'Tasa de interés',
      mortgageYears: 'Plazo de la hipoteca',
      rentMonthly: 'Alquiler mensual',
      otherRevMonthly: 'Otros ingresos',
      vacancyRate: 'Desocupación',
      propertyTaxPct: 'Impuesto a la propiedad',
      managementFeePct: 'Tarifa de administración',
      maintenanceResPct: 'Mantenimiento',
      insuranceYearly: 'Seguro',
      utilitiesMonthly: 'Servicios',
      hoaYearly: 'HOA',
      appreciationPct: 'Plusvalía',
      purchasePrice: 'Precio de compra',
      holdingMonths: 'Período de tenencia',
      salePrice: 'Precio de venta',
    },
    units: { perYear: '/año', perMonth: '/mes', years: 'años', months: 'meses' },

    toolCards: {
      update_rental_roi: { title: 'Calculadora de ROI de Alquiler', applyText: 'Aplicar a la calculadora', appliedText: 'Aplicado a la calculadora' },
      update_fix_flip: { title: 'Analizador de Fix & Flip', applyText: 'Aplicar a la calculadora', appliedText: 'Aplicado a la calculadora' },
      save_property: { title: 'Guardar propiedad', applyText: 'Guardar propiedad', appliedText: 'Propiedad guardada' },
      skip_trace_property: { title: 'Skip Trace', applyText: 'Skip Trace', appliedText: 'Skip trace iniciado' },
    },
    toolCardFallback: { title: 'Acción', applyText: 'Aplicar', appliedText: 'Aplicado' },

    openPrompt: {
      title: '¿Abrir la calculadora?',
      before: 'Para aplicar estos cambios primero hay que abrir la herramienta ',
      after: '. ¿Quieres abrirla ahora?',
      button: 'Abrir y aplicar',
    },

    viewComps: {
      title: 'Comparables y propiedades cercanas',
      prompt: 'Ve todos los datos de ventas comparables en los detalles de la propiedad.',
      openedPrompt: 'La pestaña de Comparables ya está abierta.',
      button: 'Abrir pestaña de Comparables',
      done: 'Pestaña de Comparables abierta',
    },

    actionCards: {
      open_rental_roi_calculator: {
        label: 'Calculadora de ROI de Alquiler',
        prompt: 'Abre la Calculadora de ROI de Alquiler para simular escenarios de rentabilidad.',
        button: 'Abrir Calculadora de ROI de Alquiler',
        done: 'Calculadora de ROI de Alquiler abierta',
      },
      open_fix_flip_calculator: {
        label: 'Analizador de Fix & Flip',
        prompt: 'Abre el Analizador de Fix & Flip para modelar un flip.',
        button: 'Abrir Analizador de Fix & Flip',
        done: 'Analizador de Fix & Flip abierto',
      },
    },
    actionCardFallback: { label: 'Acción', prompt: '', button: 'Abrir', done: 'Abierto' },
    actionCardOpened: (label) => `La herramienta ${label} ya está abierta.`,
  },
}

export function getChatStrings(lang) {
  return CHAT_STRINGS[lang] || CHAT_STRINGS[DEFAULT_LANG]
}

// ── Conversation language detection ───────────────────────────────────────────
// Lightweight heuristic on the user's message — only needs to tell EN from ES.
// Returns `fallback` when the message has no clear signal (e.g. "ok", "30%"),
// so the conversation keeps its current language. Words are scored rather than
// matched on a single character so an English question about e.g. "San José"
// stays English.
const SPANISH_PUNCT = /[¿¡]/
const SPANISH_ACCENTS = /[ñáéíóú]/i
const SPANISH_WORDS = /(?:^|[^A-Za-zÀ-ÿ])(qu[eé]|c[oó]mo|cu[aá]nto|cu[aá]nta|cu[aá]l|d[oó]nde|cu[aá]ndo|propiedad|propiedades|calculadora|alquiler|renta|precio|venta|ventas|vecindario|due[nñ]o|propietario|guarda|guardar|gu[aá]rdala|gu[aá]rdalo|abre|[aá]breme|abrir|muestra|mu[eé]strame|dame|dime|haz|hazme|hacer|busca|b[uú]scame|encuentra|ejecuta|corre|calcula|analiza|cambia|pon|ponle|usa|ver|quiero|puedes|podr[ií]as|necesito|hola|gracias|favor|tambi[eé]n|pero|esta|este|estos|estas|hipoteca|inter[eé]s|inversi[oó]n|ganancia|el|la|los|las|un|una|unos|unas|de|del|con|para|por|es|son|mi|tu|su|y|al|lo|se|si|s[ií]|cerca|cercanas?|similares)(?=$|[^A-Za-zÀ-ÿ])/gi
const ENGLISH_WORDS = /(?:^|[^A-Za-zÀ-ÿ])(the|is|are|was|what|how|why|where|this|that|my|i|can|could|show|save|run|open|it|me|of|and|for|with|if|should|would|do|does|please|property|price|rent|comps|nearby|calculator|yes|you)(?=$|[^A-Za-zÀ-ÿ])/gi

function countMatches(re, text) {
  return (text.match(re) || []).length
}

export function detectLang(text, fallback = DEFAULT_LANG) {
  if (!text || typeof text !== 'string') return fallback
  let es = countMatches(SPANISH_WORDS, text)
  if (SPANISH_PUNCT.test(text)) es += 2
  if (SPANISH_ACCENTS.test(text)) es += 1
  const en = countMatches(ENGLISH_WORDS, text)
  if (es > en) return 'es'
  else if (en > es) return 'en'
  else return fallback
}
